import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Check, Loader2, Film, Camera, NotebookPen, Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { EXPEDITION_STATUS, APPLICANT_ROLES } from "@/lib/expeditions";

const inputCls = "w-full bg-transparent border-b border-ptf-primary/25 py-2.5 text-sm outline-none transition-colors duration-300 focus:border-ptf-secondary placeholder:text-ptf-text/40 text-ptf-text";
const OUTPUT_ICONS = { DOCUMENTARY: Film, FILM: Film, "SHORT FILM": Film, PHOTOGRAPHY: Camera, "FIELD JOURNAL": NotebookPen };

export const ExpeditionStatusBadge = ({ status }) => {
  const meta = EXPEDITION_STATUS[status];
  if (!meta) return null;
  return <span data-testid="expedition-status-badge" className={`inline-block text-[0.6rem] font-semibold uppercase tracking-[0.14em] px-2.5 py-1 ${meta.cls}`}>{meta.label}</span>;
};

export function ExpeditionMission({ j }) {
  if (!j.mission) return null;
  return (
    <div data-testid="expedition-mission" className="py-8 border-b border-ptf-primary/12">
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-[0.65rem] uppercase tracking-[0.28em] text-ptf-primary/60">The Mission</p>
        <ExpeditionStatusBadge status={j.expedition_status} />
      </div>
      <p className="font-serif-ed italic text-2xl text-ptf-secondary leading-snug">{j.mission}</p>
      {j.outputs?.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-2" data-testid="expedition-outputs">
          {j.outputs.map((o) => {
            const Icon = OUTPUT_ICONS[o] || NotebookPen;
            return (
              <span key={o} className="inline-flex items-center gap-1.5 text-[0.62rem] uppercase tracking-[0.14em] border border-ptf-primary/25 text-ptf-primary px-3 py-1.5">
                <Icon className="h-3.5 w-3.5" /> {o}
              </span>
            );
          })}
          <span className="text-[0.6rem] text-ptf-text/45 self-center">Documented outputs of this expedition</span>
        </div>
      )}
    </div>
  );
}

function FieldJournal({ j }) {
  const { data: entries = [] } = useQuery({
    queryKey: ["journal", j.id],
    queryFn: async () => (await api.get(`/journals?journey_id=${j.id}`)).data,
  });
  const entriesWithMeta = useMemo(
    () => entries.map((e) => ({ ...e, metaLabel: [e.day && `Day ${e.day}`, e.date, e.location].filter(Boolean).join(" · ") })),
    [entries]
  );
  if (!entries.length) return null;
  const latest = entries[0];
  const inField = j.expedition_status === "IN THE FIELD";
  return (
    <div data-testid="field-journal" className="mt-10 border border-ptf-primary/12 bg-ptf-primary text-ptf-bg p-7 grain">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[0.65rem] uppercase tracking-[0.28em] text-ptf-accent">{inField ? "In the Field · Live journal" : "Field Journal"}</p>
        {latest.day && <p data-testid="journal-day" className="font-display uppercase text-2xl tracking-tight">Day {latest.day}{j.duration_days ? ` / ${j.duration_days}` : ""}</p>}
      </div>
      <div className="mt-5 space-y-6">
        {entriesWithMeta.map((e) => (
          <div key={e.id} data-testid="journal-entry" className="border-t border-ptf-bg/15 pt-4">
            <p className="text-[0.62rem] uppercase tracking-[0.14em] text-ptf-bg/55">{e.metaLabel}</p>
            <p className="mt-2 text-sm leading-relaxed text-ptf-bg/85">{e.note}</p>
            {e.image && <img src={e.image} alt={e.location || "Field photograph"} className="mt-3 w-full max-w-md object-cover border border-ptf-bg/15" loading="lazy" />}
            {e.video_url && (
              <div className="mt-3 aspect-video max-w-md border border-ptf-bg/15">
                <iframe src={e.video_url.replace("watch?v=", "embed/")} title="Field video" className="h-full w-full" allowFullScreen loading="lazy" />
              </div>
            )}
            {e.next_objective && <p className="mt-2 text-xs text-ptf-accent">Next field objective: {e.next_objective}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function ApplicationForm({ j }) {
  const [role, setRole] = useState("PARTICIPANT");
  const [form, setForm] = useState({
    name: "", email: "", phone: "", country: "India", city: "", age: "", profession: "",
    experience: "", photography_experience: "", filmmaking_experience: "", adventure_experience: "",
    wildlife_experience: "", certifications: "", motivation: "", hope_to_create: "",
    emergency_contact: "", special_requirements: "", portfolio_url: "", profile_url: "", equipment: "", heard_from: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const creative = role !== "PARTICIPANT";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/expedition-applications", { ...form, role, journey_id: j.id, journey_title: j.title });
      setDone(data.message);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send the application.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div data-testid="expedition-app-success" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="py-4">
        <span className="grid place-items-center h-12 w-12 rounded-full bg-ptf-accent text-ptf-primary mb-5"><Check className="h-6 w-6" /></span>
        <h3 className="font-display uppercase text-2xl tracking-tight text-ptf-primary">Application received.</h3>
        <p className="mt-3 text-sm text-ptf-text/75 leading-relaxed max-w-md">{done}</p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="expedition-application-form" className="space-y-5">
      <div>
        <p className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/55 mb-3">I want to join as</p>
        <div className="flex flex-wrap gap-2">
          {APPLICANT_ROLES.map((r) => (
            <button key={r} type="button" data-testid={`role-${r.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setRole(r)}
              className={`text-[0.62rem] uppercase tracking-[0.12em] px-3 py-1.5 border transition-colors ${role === r ? "bg-ptf-primary text-ptf-bg border-ptf-primary" : "border-ptf-primary/25 text-ptf-text/70 hover:border-ptf-primary"}`}>
              {r}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <input required data-testid="ea-name" value={form.name} onChange={set("name")} placeholder="Full name *" className={inputCls} />
        <input required type="email" data-testid="ea-email" value={form.email} onChange={set("email")} placeholder="Email *" className={inputCls} />
        <input data-testid="ea-phone" value={form.phone} onChange={set("phone")} placeholder="Phone" className={inputCls} />
        <input data-testid="ea-age" value={form.age} onChange={set("age")} placeholder="Age" className={inputCls} />
        <input data-testid="ea-country" value={form.country} onChange={set("country")} placeholder="Country" className={inputCls} />
        <input data-testid="ea-city" value={form.city} onChange={set("city")} placeholder="City" className={inputCls} />
        <input data-testid="ea-profession" value={form.profession} onChange={set("profession")} placeholder="Profession" className={inputCls} />
        <input data-testid="ea-emergency" value={form.emergency_contact} onChange={set("emergency_contact")} placeholder="Emergency contact" className={inputCls} />
        <input data-testid="ea-photo-exp" value={form.photography_experience} onChange={set("photography_experience")} placeholder="Photography experience" className={inputCls} />
        <input data-testid="ea-film-exp" value={form.filmmaking_experience} onChange={set("filmmaking_experience")} placeholder="Filmmaking experience" className={inputCls} />
        <input data-testid="ea-adventure-exp" value={form.adventure_experience} onChange={set("adventure_experience")} placeholder="Adventure experience" className={inputCls} />
        <input data-testid="ea-wildlife-exp" value={form.wildlife_experience} onChange={set("wildlife_experience")} placeholder="Wildlife experience" className={inputCls} />
        <input data-testid="ea-certs" value={form.certifications} onChange={set("certifications")} placeholder="Relevant certifications" className={inputCls} />
        <input data-testid="ea-heard" value={form.heard_from} onChange={set("heard_from")} placeholder="How did you hear about Planet Trotter?" className={inputCls} />
      </div>
      {creative && (
        <div className="border border-ptf-primary/12 bg-ptf-primary/[0.03] p-5 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="creative-fields">
          <input data-testid="ea-portfolio" value={form.portfolio_url} onChange={set("portfolio_url")} placeholder="Portfolio / website (URL)" className={inputCls} />
          <input data-testid="ea-profile" value={form.profile_url} onChange={set("profile_url")} placeholder="Social / professional profile (URL)" className={inputCls} />
          <input data-testid="ea-equipment" value={form.equipment} onChange={set("equipment")} placeholder="Equipment" className={inputCls} />
          <input data-testid="ea-create" value={form.hope_to_create} onChange={set("hope_to_create")} placeholder="What do you hope to create?" className={inputCls} />
        </div>
      )}
      <textarea required data-testid="ea-motivation" rows={3} value={form.motivation} onChange={set("motivation")} placeholder="Why do you want to join this expedition? *" className={`${inputCls} resize-none`} />
      <input data-testid="ea-special" value={form.special_requirements} onChange={set("special_requirements")} placeholder="Special requirements (optional)" className={inputCls} />
      <button type="submit" data-testid="ea-submit" disabled={loading} className="bg-ptf-primary text-ptf-bg px-10 py-3.5 text-[0.72rem] font-semibold uppercase tracking-[0.18em] hover:bg-ptf-secondary transition-colors disabled:opacity-60 inline-flex items-center gap-2">
        {loading && <Loader2 className="h-4 w-4 animate-spin" />} Apply for the Expedition
      </button>
    </form>
  );
}

export function ExpeditionDossier({ j, hideApplicationForm = false }) {
  return (
    <>
      {j.team?.length > 0 && (
        <div data-testid="expedition-team" className="mt-10 border border-ptf-primary/12 bg-white p-7">
          <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-primary/50 mb-4 flex items-center gap-2"><Users className="h-4 w-4" /> The field team</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {j.team.map((m) => (
              <div key={`${m.role}-${m.name}`} className="border-t border-ptf-primary/10 pt-3">
                <p className="text-[0.6rem] uppercase tracking-[0.16em] text-ptf-text/50">{m.role}</p>
                <p className="font-display uppercase text-lg tracking-tight text-ptf-primary">{m.name}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {j.eligibility?.length > 0 && (
        <div data-testid="expedition-eligibility" className="mt-10 border border-ptf-primary/12 bg-white p-7">
          <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-primary/50 mb-3">Who can join?</p>
          <div className="flex flex-wrap gap-2">
            {j.eligibility.map((e) => <span key={e} className="text-[0.65rem] uppercase tracking-[0.12em] bg-ptf-primary/5 text-ptf-primary px-3 py-1.5">{e}</span>)}
          </div>
          <p className="mt-3 text-[0.62rem] text-ptf-text/50">Eligibility is set per expedition; applications are reviewed by the expedition team.</p>
        </div>
      )}
      <FieldJournal j={j} />
      {!hideApplicationForm && (
        <div id="expedition-apply" className="mt-10 border border-ptf-primary/12 bg-white p-7 md:p-9">
          <p className="text-[0.65rem] uppercase tracking-[0.28em] text-ptf-primary/60 mb-2">Expedition application</p>
          <h2 className="font-display font-extrabold uppercase text-3xl tracking-tight text-ptf-primary">Go further. Come back with a story.</h2>
          <p className="mt-3 text-sm text-ptf-text/70 leading-relaxed max-w-xl">
            Apply as a participant, or as a photographer, filmmaker, writer, researcher,
            naturalist or field assistant. Paid participation is handled through the
            participation card; this application is how the team gets to know you.
          </p>
          <div className="mt-7">
            <ApplicationForm j={j} />
          </div>
        </div>
      )}
    </>
  );
}
